import { type ReactNode } from "react";

/**
 * DashboardGrid — the observability kit's responsive grid shell (observability-ui task 1.6; design
 * §Components and Interfaces §5). It lays the kit's cards out like the Netdata overview in spirit
 * (adapted, not copied): a KPI row across the top, then the chart cards, then status/alerts — using
 * only the theme-tokened grid primitives in `observability.css` (`.obs-grid`, `.obs-grid__kpis`,
 * `.obs-grid__full`). Gaps and borders come from the theme tokens, so the shell reads the same in
 * `:root[data-theme="dark"]` and `:root[data-theme="light"]` with no code change, and there is no
 * permanently-dark surface (Requirements 1.1, 2.1).
 *
 * The shell is presentational only: it owns layout, not data — a screen passes already-built kit
 * components (KpiCards, ChartCards, StatusGrid, AlertsPanel) as children/slots and the grid places
 * them. The tracks auto-fill and collapse to a single column on narrow viewports (the `.obs-grid`
 * / `.obs-grid__kpis` media query), so the dashboard wraps gracefully rather than overflowing.
 *
 * Accessibility (Requirement 1.3): the whole dashboard is a labelled `region`. A caller supplies a
 * `label` (an accessible name via `aria-label`) or, when the heading is already rendered elsewhere
 * on the page, an `aria-labelledby` id; the region is keyboard-reachable in document order and adds
 * no focus trap of its own. The KPI row and the chart/status area are themselves nested, labelled
 * `group`s so assistive tech can skim the dashboard's structure.
 */

export interface DashboardGridProps {
  /**
   * Accessible name for the dashboard region. Provide this OR {@link labelledBy}; when both are
   * given, `labelledBy` wins (it points at a visible heading, which is preferred). When neither is
   * given the region still renders but is unlabelled — callers should always name it.
   */
  readonly label?: string;
  /**
   * The id of a visible heading that names this dashboard (sets `aria-labelledby`). Prefer this
   * over {@link label} when a heading is already on the page.
   */
  readonly labelledBy?: string;
  /**
   * The KPI row — a set of KpiCards laid out in the dense `.obs-grid__kpis` track (auto-fills,
   * collapses to one column when narrow). Omit (or pass nothing) to drop the row entirely.
   */
  readonly kpis?: ReactNode;
  /**
   * The main dashboard body — chart cards, status grid and alerts panel. Rendered in the
   * auto-filling `.obs-grid` track. A child that should span the full width (a wide chart, the
   * service map, an alerts panel) wraps itself in `.obs-grid__full` (see {@link DashboardGridFull}).
   */
  readonly children?: ReactNode;
  /** Optional extra class on the outer region (e.g. a screen-scoped spacing modifier). */
  readonly className?: string;
}

export function DashboardGrid(props: DashboardGridProps): ReactNode {
  const hasKpis = props.kpis !== undefined && props.kpis !== null && props.kpis !== false;
  const className = ["obs-dashboard", props.className].filter(Boolean).join(" ");
  return (
    <section
      className={className}
      // A labelled region so the dashboard is announced as one landmark (Requirement 1.3). Prefer a
      // visible heading via `aria-labelledby`; fall back to an `aria-label` string.
      aria-label={props.labelledBy === undefined ? props.label : undefined}
      aria-labelledby={props.labelledBy}
    >
      {hasKpis && (
        <div className="obs-grid__kpis" role="group" aria-label="Key metrics">
          {props.kpis}
        </div>
      )}
      <div className="obs-grid" role="group" aria-label="Charts and status">
        {props.children}
      </div>
    </section>
  );
}

/**
 * A thin wrapper that makes its child span the full dashboard-grid width (the `.obs-grid__full`
 * primitive). Use it inside {@link DashboardGrid}'s `children` for a wide chart, the service map, or
 * the alerts panel that should take the whole row rather than a single auto-filled track.
 */
export interface DashboardGridFullProps {
  readonly children?: ReactNode;
}

export function DashboardGridFull(props: DashboardGridFullProps): ReactNode {
  return <div className="obs-grid__full">{props.children}</div>;
}
